import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Collapse,
  useScrollTrigger,
  alpha,
  Divider,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Link as RouterLink, useLocation } from 'react-router';

const gold = '#C0202A';
const silver = '#C0C0C0';

const serviceLinks = [
  { label: 'Auto & Marine Detailing', path: '/services/auto-detailing' },
  { label: 'Carpet Cleaning & Restoration', path: '/services/carpet-cleaning' },
  { label: 'Tile Cleaning & Restoration', path: '/services/tile-grout' },
  { label: 'Upholstery & Leather Care', path: '/services/upholstery-leather' },
];

const navLinks = [
  { label: 'Home', path: '/' },
  { label: 'Services', path: '/services', hasDropdown: true },
  { label: 'Why It Matters', path: '/why-it-matters' },
  { label: 'Gallery', path: '/gallery' },
  { label: 'Contact', path: '/contact' },
];

export function Navbar() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [servicesHover, setServicesHover] = useState(false);
  const location = useLocation();

  const trigger = useScrollTrigger({ disableHysteresis: true, threshold: 60 });
  const isHome = location.pathname === '/';

  const handleNav = (path: string) => {
    setDrawerOpen(false);
    setServicesHover(false);
  };

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          background: trigger
            ? alpha('#080808', 0.97)
            : isHome
            ? 'linear-gradient(to bottom, rgba(0,0,0,0.55) 0%, transparent 100%)'
            : 'transparent',
          backdropFilter: trigger ? 'blur(20px)' : 'none',
          borderBottom: trigger ? `1px solid ${alpha(gold, 0.15)}` : 'none',
          transition: 'all 0.4s ease',
          zIndex: 1300,
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 2, md: 6 }, py: 1 }}>
          {/* Logo */}
          <Box
            component={RouterLink}
            to="/"
            aria-label="Go to home page"
            onClick={() => handleNav('/')}
            sx={{
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              background: 'transparent',
              border: 0,
              p: 0,
              textDecoration: 'none',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 3,
                  height: 36,
                  background: `linear-gradient(180deg, ${gold} 0%, ${alpha(gold, 0.3)} 100%)`,
                  mr: 0.5,
                  flexShrink: 0,
                }}
              />
              <Box>
                <Box
                  component="span"
                  sx={{
                    fontFamily: '"Cormorant Garamond", serif',
                    fontSize: '1.6rem',
                    fontWeight: 300,
                    letterSpacing: '0.12em',
                    color: '#F2EDE4',
                    display: 'block',
                    lineHeight: 1.1,
                  }}
                >
                  LINDSEY
                </Box>
                <Box
                  component="span"
                  sx={{
                    fontFamily: '"Montserrat", sans-serif',
                    fontSize: '0.55rem',
                    fontWeight: 600,
                    letterSpacing: '0.3em',
                    color: gold,
                    textTransform: 'uppercase',
                    display: 'block',
                  }}
                >
                  ENTERPRISES
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Desktop Nav */}
          <Box
            component="nav"
            aria-label="Primary navigation"
            sx={{ display: { xs: 'none', lg: 'flex' }, alignItems: 'center', gap: 0.5 }}
          >
            {navLinks.map((link) => {
              if (link.hasDropdown) {
                return (
                  <Box
                    key={link.label}
                    onMouseEnter={() => setServicesHover(true)}
                    onMouseLeave={() => setServicesHover(false)}
                    sx={{ position: 'relative' }}
                  >
                    <Button
                      component={RouterLink}
                      to={link.path}
                      endIcon={<KeyboardArrowDownIcon sx={{ fontSize: '0.9rem !important', transition: 'transform 0.3s', transform: servicesHover ? 'rotate(180deg)' : 'none' }} />}
                      sx={{
                        color: isActive(link.path) ? gold : alpha('#F2EDE4', 0.85),
                        fontFamily: '"Montserrat", sans-serif',
                        fontWeight: 500,
                        fontSize: '0.7rem',
                        letterSpacing: '0.15em',
                        textTransform: 'uppercase',
                        px: 2,
                        py: 1,
                        borderBottom: isActive(link.path) ? `1px solid ${gold}` : '1px solid transparent',
                        borderRadius: 0,
                        '&:hover': { color: gold, backgroundColor: 'transparent' },
                      }}
                    >
                      {link.label}
                    </Button>
                    {/* Dropdown */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '100%',
                        left: '50%',
                        mt: 1,
                        minWidth: 260,
                        background: alpha('#0D0D0D', 0.98),
                        backdropFilter: 'blur(20px)',
                        border: `1px solid ${alpha(gold, 0.2)}`,
                        opacity: servicesHover ? 1 : 0,
                        visibility: servicesHover ? 'visible' : 'hidden',
                        transition: 'all 0.25s ease',
                        transform: servicesHover ? 'translateX(-50%) translateY(0)' : 'translateX(-50%) translateY(-8px)',
                        zIndex: 1400,
                      }}
                    >
                      {serviceLinks.map((sl, i) => (
                        <Box key={sl.label}>
                          <Box
                            component={RouterLink}
                            to={sl.path}
                            onClick={() => handleNav(sl.path)}
                            sx={{
                              px: 3,
                              py: 1.8,
                              cursor: 'pointer',
                              fontFamily: '"Montserrat", sans-serif',
                              fontSize: '0.68rem',
                              fontWeight: 500,
                              letterSpacing: '0.1em',
                              textTransform: 'uppercase',
                              whiteSpace: 'nowrap',
                              color: isActive(sl.path) ? gold : alpha('#F2EDE4', 0.8),
                              transition: 'all 0.2s',
                              textDecoration: 'none',
                              '&:hover': {
                                color: gold,
                                backgroundColor: alpha(gold, 0.05),
                                transform: 'translateX(8px)',
                              },
                            }}
                          >
                            {sl.label}
                          </Box>
                          {i < serviceLinks.length - 1 && (
                            <Divider sx={{ borderColor: alpha(gold, 0.08) }} />
                          )}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                );
              }
              return (
                <Button
                  component={RouterLink}
                  to={link.path}
                  key={link.label}
                  onClick={() => handleNav(link.path)}
                  sx={{
                    color: isActive(link.path) ? gold : alpha('#F2EDE4', 0.85),
                    fontFamily: '"Montserrat", sans-serif',
                    fontWeight: 500,
                    fontSize: '0.7rem',
                    letterSpacing: '0.15em',
                    textTransform: 'uppercase',
                    px: 2,
                    py: 1,
                    borderBottom: isActive(link.path) ? `1px solid ${gold}` : '1px solid transparent',
                    borderRadius: 0,
                    '&:hover': { color: gold, backgroundColor: 'transparent' },
                  }}
                >
                  {link.label}
                </Button>
              );
            })}
          </Box>

          {/* CTA Button */}
          <Box sx={{ display: { xs: 'none', lg: 'flex' }, alignItems: 'center', gap: 2 }}>
            <Button
              variant="outlined"
              color="primary"
              component={RouterLink}
              to="/contact"
              onClick={() => handleNav('/contact')}
              sx={{
                fontSize: '0.65rem',
                letterSpacing: '0.2em',
                py: 1,
                px: 3,
              }}
            >
              Request Service
            </Button>
          </Box>

          {/* Mobile Menu Button */}
          <IconButton
            aria-label={drawerOpen ? 'Close navigation menu' : 'Open navigation menu'}
            sx={{ display: { xs: 'flex', lg: 'none' }, color: gold, zIndex: 1300 }}
            onClick={() => setDrawerOpen((open) => !open)}
          >
            {drawerOpen ? <CloseIcon /> : <MenuIcon />}
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: 300,
            background: '#0A0A0A',
            borderLeft: `1px solid ${alpha(gold, 0.15)}`,
          },
        }}
      >
        <Box sx={{ height: 72 }} />
        <List sx={{ py: 2 }}>
          {navLinks.map((link) => {
            if (link.hasDropdown) {
              return (
                <React.Fragment key={link.label}>
                  <ListItemButton
                    onClick={() => setServicesOpen(!servicesOpen)}
                    sx={{ px: 4 }}
                  >
                    <ListItemText
                      primary={link.label}
                      primaryTypographyProps={{
                        fontFamily: '"Montserrat", sans-serif',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        letterSpacing: '0.15em',
                        textTransform: 'uppercase',
                        color: isActive(link.path) ? gold : '#F2EDE4',
                      }}
                    />
                    {servicesOpen ? <ExpandLess sx={{ color: gold }} /> : <ExpandMore sx={{ color: alpha('#F2EDE4', 0.5) }} />}
                  </ListItemButton>
                  <Collapse in={servicesOpen}>
                    <List disablePadding>
                      {serviceLinks.map((sl) => (
                        <ListItemButton
                          component={RouterLink}
                          to={sl.path}
                          key={sl.label}
                          onClick={() => handleNav(sl.path)}
                          sx={{ pl: 6, py: 1 }}
                        >
                          <ListItemText
                            primary={sl.label}
                            primaryTypographyProps={{
                              fontFamily: '"Montserrat", sans-serif',
                              fontSize: '0.65rem',
                              fontWeight: 500,
                              letterSpacing: '0.1em',
                              textTransform: 'uppercase',
                              color: isActive(sl.path) ? gold : alpha('#F2EDE4', 0.7),
                            }}
                          />
                        </ListItemButton>
                      ))}
                    </List>
                  </Collapse>
                </React.Fragment>
              );
            }
            return (
              <ListItemButton
                key={link.label}
                component={RouterLink}
                to={link.path}
                onClick={() => handleNav(link.path)}
                sx={{ px: 4, py: 1.5 }}
              >
                <ListItemText
                  primary={link.label}
                  primaryTypographyProps={{
                    fontFamily: '"Montserrat", sans-serif',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    letterSpacing: '0.15em',
                    textTransform: 'uppercase',
                    color: isActive(link.path) ? gold : '#F2EDE4',
                  }}
                />
              </ListItemButton>
            );
          })}
        </List>
        <Box sx={{ p: 4, mt: 'auto' }}>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            component={RouterLink}
            to="/contact"
            onClick={() => handleNav('/contact')}
            sx={{ py: 1.5, fontSize: '0.7rem', letterSpacing: '0.15em' }}
          >
            Request Service
          </Button>
        </Box>
      </Drawer>
    </>
  );
}
